import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchMe, resendVerification } from '../lib/api';
import type { ApiError } from '../lib/api';

/**
 * Slim, dismissible "verify your email" banner for the authed app shell.
 *
 * Renders ONLY when `GET /api/me` reports `email_verified === false`; the moment
 * a later `/api/me` flips it to `true` (verification landed) the banner vanishes.
 * Reuses the shared `['me']` query the AuthGate already populates, so it adds no
 * extra request. Calm + forgiving (never alarming): Resend surfaces a subtle
 * "sent" confirmation on 200 and a gentle "wait a moment" on a 429 rate-limit,
 * keeping the button available to retry.
 *
 * Dismissal is local `useState`: this banner is mounted in the always-mounted
 * app-shell (in-app moves to /org and /app are full page loads), so dismissing
 * hides it for the session yet a reload re-mounts it — reappearing while the
 * account is still unverified, exactly as specced.
 */
export function VerifyEmailBanner() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const [dismissed, setDismissed] = useState(false);

  const resend = useMutation({
    mutationFn: () => resendVerification(data!.email),
  });

  // Only a confirmed-unverified session shows the banner (an absent flag on an
  // older cached payload is treated as verified — never nag on uncertainty).
  if (dismissed || !data || data.email_verified !== false) return null;

  const err = resend.error as ApiError | null;

  return (
    <div className="verify-email-banner" role="status" aria-live="polite">
      <span className="verify-email-banner-msg">
        <span className="verify-email-banner-icon" aria-hidden="true">
          ✉
        </span>{' '}
        Verify your email — sent to <strong>{data.email}</strong>
      </span>

      {resend.isSuccess ? (
        <span className="verify-email-banner-sent">Sent — check your inbox</span>
      ) : (
        <span className="verify-email-banner-actions">
          {err && (
            <span className="verify-email-banner-note">
              {err.status === 429
                ? 'Please wait a moment before resending.'
                : 'Couldn’t resend just now — try again shortly.'}
            </span>
          )}
          <button
            type="button"
            className="verify-email-banner-resend"
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
          >
            {resend.isPending ? 'Sending…' : 'Resend'}
          </button>
        </span>
      )}

      <button
        type="button"
        className="verify-email-banner-dismiss"
        aria-label="Dismiss verify-email reminder"
        onClick={() => setDismissed(true)}
      >
        ✕
      </button>
    </div>
  );
}
